import { useEffect, useState } from 'react';
import { useForm, type UseFormSetError } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { dealerOverrideAssignmentSchema, productSlotAssignmentSchema } from '../schemas/templateAssignment.schema';
import type { AssignTemplateErrors, AssignTemplateMode, AssignTemplateValues } from '../components/ProductDocuments/AssignTemplateModal';
import type { TemplateAssignment } from '../types/templateAssignments.types';
import type { AssignableDocumentType } from '../types/documentTemplates.types';
import { applyServerFieldErrors } from '../utils/formErrors';
import { useAssignTemplateMutation, useChangeAssignedTemplateMutation } from './useTemplateAssignmentMutations';

export type AssignTarget =
  | { kind: 'product-slot'; documentType: AssignableDocumentType }
  | { kind: 'dealer-override' }
  | { kind: 'change'; assignment: TemplateAssignment };

const RENDERED_FIELDS: Record<AssignTemplateMode, ReadonlyArray<keyof AssignTemplateValues>> = {
  'product-slot': ['templateId'],
  change: ['templateId'],
  'dealer-override': ['dealerId', 'documentType', 'templateId'],
};

const SERVER_FIELD_MAP = { document_template_id: 'templateId' };

const modeOf = (target: AssignTarget): AssignTemplateMode => target.kind;

function defaultsFor(target: AssignTarget | null): AssignTemplateValues {
  if (target?.kind === 'product-slot') return { dealerId: '', documentType: target.documentType, templateId: '' };
  if (target?.kind === 'change') {
    const { dealerId, documentType, template } = target.assignment;
    return { dealerId: dealerId ?? '', documentType, templateId: template.id };
  }
  return { dealerId: '', documentType: '', templateId: '' };
}

export function useAssignTemplateForm(productId: string, target: AssignTarget | null, onSaved: () => void) {
  const assign = useAssignTemplateMutation();
  const change = useChangeAssignedTemplateMutation();
  const [formError, setFormError] = useState<string | undefined>();
  const mode = target ? modeOf(target) : 'product-slot';
  const form = useForm<AssignTemplateValues>({
    mode: 'onBlur',
    resolver: zodResolver(mode === 'dealer-override' ? dealerOverrideAssignmentSchema : productSlotAssignmentSchema),
    defaultValues: defaultsFor(target),
  });

  useEffect(() => {
    form.reset(defaultsFor(target));
    setFormError(undefined);
  }, [target]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFailure = (error: unknown) => {
    const rendered = RENDERED_FIELDS[mode];
    const routeError: UseFormSetError<AssignTemplateValues> = (field, failure) => {
      if (rendered.includes(field as keyof AssignTemplateValues)) form.setError(field, failure);
      else setFormError(failure.message);
    };
    if (!applyServerFieldErrors(error, routeError, SERVER_FIELD_MAP)) setFormError((error as Error).message);
  };

  const submit = form.handleSubmit((values) => {
    if (!target) return;
    setFormError(undefined);
    const options = { onSuccess: onSaved, onError: handleFailure };
    if (target.kind === 'change') {
      change.mutate({ assignmentId: target.assignment.id, templateId: values.templateId }, options);
      return;
    }
    assign.mutate(
      {
        productId,
        dealerId: target.kind === 'dealer-override' ? values.dealerId : null,
        documentType: values.documentType as AssignableDocumentType,
        templateId: values.templateId,
      },
      options,
    );
  });

  const setValues = (patch: Partial<AssignTemplateValues>) => {
    const resetsTemplate = patch.dealerId !== undefined || patch.documentType !== undefined;
    (Object.entries(patch) as [keyof AssignTemplateValues, string][]).forEach(([field, value]) => {
      form.setValue(field, value as never, { shouldDirty: true });
      form.clearErrors(field);
    });
    if (resetsTemplate && patch.templateId === undefined) form.setValue('templateId', '');
  };

  const fieldErrors = form.formState.errors;
  const errors: AssignTemplateErrors = {
    dealerId: fieldErrors.dealerId?.message,
    documentType: fieldErrors.documentType?.message,
    templateId: fieldErrors.templateId?.message,
  };

  return {
    mode,
    values: form.watch(),
    errors,
    formError,
    setValues,
    submit,
    isSaving: assign.isPending || change.isPending,
  };
}
